import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';

export interface TaskHistoryItem {
  task_id: string;
  user_id: number | null;
  username: string | null;
  task_type: string;
  backend: string | null;
  model_used: string | null;
  priority: string | null;
  status: string;
  duration: number | null;
  tokens_used: number;
  input_summary: string | null;
  error_message: string | null;
  created_at: string | null;
}

interface RecentTaskListProps {
  tasks: TaskHistoryItem[];
  onTaskClick: (taskId: string) => void;
  onLoadMore: () => void;
  hasMore: boolean;
}

const statusBadge = (status: string) => {
  if (status === 'completed') return <Badge variant="success" className="text-xs">완료</Badge>;
  if (status === 'failed') return <Badge variant="error" className="text-xs">실패</Badge>;
  return <Badge variant="default" className="text-xs">{status}</Badge>;
};

const priorityBadge = (priority: string | null) => {
  if (priority === 'high') return <Badge variant="error" className="text-xs">High</Badge>;
  if (priority === 'mid') return <Badge variant="warning" className="text-xs">Mid</Badge>;
  return <Badge variant="default" className="text-xs">{priority || '-'}</Badge>;
};

const RecentTaskList: React.FC<RecentTaskListProps> = ({
  tasks, onTaskClick, onLoadMore, hasMore,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>최근 작업 목록</CardTitle>
      </CardHeader>
      <CardContent>
        {tasks.length === 0 ? (
          <p className="text-center text-gray-500 py-8">작업 이력이 없습니다.</p>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-gray-500">
                    <th className="pb-2 pr-4">Task ID</th>
                    <th className="pb-2 pr-4">요청자</th>
                    <th className="pb-2 pr-4">유형</th>
                    <th className="pb-2 pr-4">Provider</th>
                    <th className="pb-2 pr-4">우선순위</th>
                    <th className="pb-2 pr-4">소요시간</th>
                    <th className="pb-2 pr-4">상태</th>
                    <th className="pb-2">시간</th>
                  </tr>
                </thead>
                <tbody>
                  {tasks.map((task) => (
                    <tr
                      key={task.task_id}
                      className="border-b hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={() => onTaskClick(task.task_id)}
                    >
                      <td className="py-2 pr-4 font-mono text-xs text-gray-600">
                        {task.task_id.substring(0, 8)}...
                      </td>
                      <td className="py-2 pr-4">{task.username || '-'}</td>
                      <td className="py-2 pr-4">
                        <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                          {task.task_type}
                        </span>
                      </td>
                      <td className="py-2 pr-4 text-xs">{task.backend || '-'}</td>
                      <td className="py-2 pr-4">{priorityBadge(task.priority)}</td>
                      <td className="py-2 pr-4 text-xs">
                        {task.duration !== null ? `${task.duration.toFixed(2)}s` : '-'}
                      </td>
                      <td className="py-2 pr-4">{statusBadge(task.status)}</td>
                      <td className="py-2 text-xs text-gray-500">
                        {task.created_at ? new Date(task.created_at).toLocaleString('ko-KR') : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {hasMore && (
              <div className="mt-4 text-center">
                <Button variant="outline" size="sm" onClick={onLoadMore}>
                  더 보기
                </Button>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default RecentTaskList;
